import axios from "axios";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

// API Base URL - using the actual endpoint from assets/apiConfig.js
const API_BASE_URL = "https://techmindinfosys.com/server/api";

// API Endpoints configuration
export const API_ENDPOINTS = {
  BASE: API_BASE_URL,
  JOBS: "/jobs.php",
  JOB_BY_ID: (id) => `/jobs.php?id=${id}`,
  MODERATOR_LOGIN: "/moderator/login.php",
  MODERATOR_DASHBOARD: "/moderator/dashboard.php",
  APPLICATIONS: "/applications.php",
  APPLICATION_BY_ID: (id) => `/applications.php?id=${id}`,
  JOB_APPLICATIONS: "/api/job-applications.php",
  JOB_APPLICATION_BY_ID: (id) => `/api/job-applications.php?id=${id}`,
  CV_DOCUMENTS: "/cv-documents.php",
  CV_BY_ID: (id) => `/cv-documents.php?id=${id}`,
  UPLOAD_CV: "/cv-documents/upload.php",
  USER: "/user.php",
  LOGIN: "/auth/login.php",
  REGISTER: "/auth/register.php",
  VERIFY_EMAIL: "/auth/verify-email.php",
  RESET_PASSWORD: "/auth/reset-password.php",
  SEND_RESET_EMAIL: "/auth/send-reset-email.php",
  JOB_MATCH_NOTIFY: "/job-match-notify.php",
  DIRECT_APPLICATIONS: (jobId) => `/direct-applications.php?job_id=${jobId}`,
};

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("authToken");
    const moderatorToken = localStorage.getItem("moderator_token");

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else if (moderatorToken) {
      config.headers.Authorization = `Bearer ${moderatorToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.log(error);
    if (error.response?.status === 401) {
      localStorage.removeItem("authToken");
      localStorage.removeItem("moderator_token");
      localStorage.removeItem("user");
      window.location.href = "/moderator-login";
    }

    const message =
      error.response?.data?.message || error.message || "Something went wrong";
    toast.error(message);
    return Promise.reject(error);
  }
);

// Generic API function (matching the structure from assets/apiConfig.js)
export const apiRequest = async (
  endpoint,
  method = "GET",
  data = null,
  requireAuth = false
) => {
  try {
    const url = `${API_BASE_URL}${endpoint}`;
    const config = {
      method,
      headers: {
        "Content-Type": "application/json",
        ...(requireAuth && {
          Authorization: `Bearer ${
            localStorage.getItem("moderator_token") ||
            localStorage.getItem("authToken")
          }`,
        }),
      },
    };

    if (method !== "GET" && data) {
      config.body = JSON.stringify(data);
    }
    const response = await fetch(url, config);

    if (!response.ok) {
      if (response) {
        if (response.status == 401) {
          localStorage.removeItem("moderator_token");
          localStorage.removeItem("moderator_user");
          window.location.href = "/moderator-login";
        }
      }
      throw new Error(response?.message || "API request failed");
    }
    const result = await response.json();

    return result;
  } catch (error) {
    console.log("ccc", error);

    throw error;
  }
};

// Auth Services
export const authAPI = {
  login: (credentials) => apiRequest(API_ENDPOINTS.LOGIN, "POST", credentials),
  register: (userData) => apiRequest(API_ENDPOINTS.REGISTER, "POST", userData),
  verifyEmail: (token) =>
    apiRequest(API_ENDPOINTS.VERIFY_EMAIL, "POST", { token }),
  resetPassword: (token, password) =>
    apiRequest(API_ENDPOINTS.RESET_PASSWORD, "POST", { token, password }),
  sendResetEmail: (email) =>
    apiRequest(API_ENDPOINTS.SEND_RESET_EMAIL, "POST", { email }),
};

// Job Services
export const jobAPI = {
  getAllJobs: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(
      `${API_ENDPOINTS.JOBS}${queryString ? `?${queryString}` : ""}`
    );
  },
  getJobById: (id) => apiRequest(API_ENDPOINTS.JOB_BY_ID(id)),
  createJob: (jobData) => apiRequest(API_ENDPOINTS.JOBS, "POST", jobData, true),
  updateJob: (id, jobData) =>
    apiRequest(API_ENDPOINTS.JOB_BY_ID(id), "PUT", jobData, true),
  deleteJob: (id) =>
    apiRequest(API_ENDPOINTS.JOB_BY_ID(id), "DELETE", { id }, true),
  applyForJob: async (jobId, applicationData) => {
    const formData = new FormData();
    Object.keys(applicationData).forEach((key) => {
      formData.append(key, applicationData[key]);
    });
    return fetch(`${API_BASE_URL}${API_ENDPOINTS.DIRECT_APPLICATIONS(jobId)}`, {
      method: "POST",
      body: formData,
    }).then((res) => res.json());
  },
  getJobApplications: (jobId) =>
    apiRequest(API_ENDPOINTS.JOB_APPLICATION_BY_ID(jobId), "GET", null, true),
  updateApplicationStatus: (jobId, applicationId, status) =>
    apiRequest(
      API_ENDPOINTS.JOB_APPLICATION_BY_ID(applicationId),
      "PATCH",
      { status },
      true
    ),
};

// User Services
export const userAPI = {
  getProfile: () => apiRequest(API_ENDPOINTS.USER, "GET", null, true),
  updateProfile: (userData) =>
    apiRequest(API_ENDPOINTS.USER, "PUT", userData, true),
  uploadCV: async (formData) => {
    return fetch(`${API_BASE_URL}${API_ENDPOINTS.UPLOAD_CV}`, {
      method: "POST",
      body: formData,
    }).then((res) => res.json());
  },
  // getApplications: () => apiRequest(API_ENDPOINTS.APPLICATIONS, 'GET', null, true),
  deleteApplication: (applicationId) =>
    apiRequest(
      API_ENDPOINTS.APPLICATION_BY_ID(applicationId),
      "DELETE",
      null,
      true
    ),
};

// CV Document Services
export const cvAPI = {
  // getCVDocuments: () => apiRequest(API_ENDPOINTS.CV_DOCUMENTS, 'GET', null, true),
  getCVDocuments: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(
      `${API_ENDPOINTS.CV_DOCUMENTS}${queryString ? `?${queryString}` : ""}`,
      "GET",
      null,
      true
    );
  },
  updateCVById: (id, data) =>  apiRequest(API_ENDPOINTS.CV_BY_ID(id), "PUT", data, true),
  getCVById: (id) => apiRequest(API_ENDPOINTS.CV_BY_ID(id), "GET", null, true),
  uploadCV: async (formData) => {
    return fetch(`${API_BASE_URL}${API_ENDPOINTS.UPLOAD_CV}`, {
      method: "POST",
      body: formData,
    }).then((res) => res.json());
  },
  deleteCV: (id) => apiRequest(API_ENDPOINTS.CV_BY_ID(id), "DELETE", { id }, true),
  downloadCV: (id) => apiRequest(`/download.php?id=${id}`, "GET", { id }, true),
};

// Moderator Services
export const moderatorAPI = {
  login: (credentials) =>
    apiRequest(API_ENDPOINTS.MODERATOR_LOGIN, "POST", credentials),
  // getDashboard: () => apiRequest(API_ENDPOINTS.MODERATOR_DASHBOARD, 'GET', null, true),
  checkAuth: async () => {
    try {
      if (!localStorage.getItem("moderator_token")) return false;
      // const result = await apiRequest(API_ENDPOINTS.MODERATOR_DASHBOARD, 'GET', null, true);
      return true;
    } catch (error) {
      return false;
    }
  },
};

// Application Services
export const applicationAPI = {
  // getAllApplications: () => apiRequest(API_ENDPOINTS.APPLICATIONS, 'GET', null, true),
  getAllApplications: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(
      `${API_ENDPOINTS.APPLICATIONS}${queryString ? `?${queryString}` : ""}`,
      "GET",
      null,
      true
    );
  },
  getApplicationById: (id) =>
    apiRequest(API_ENDPOINTS.APPLICATION_BY_ID(id), "GET", null, true),
  updateApplication: (id, data) =>
    apiRequest(API_ENDPOINTS.APPLICATION_BY_ID(id), "PUT", data, true),
  deleteApplication: (id) =>
    apiRequest(API_ENDPOINTS.APPLICATION_BY_ID(id), "DELETE", null, true),
  getJobApplications: (jobId) =>
    apiRequest(API_ENDPOINTS.JOB_APPLICATION_BY_ID(jobId), "GET", null, true),
};

// Job Match Services
export const jobMatchAPI = {
  notifyMatch: (data) =>
    apiRequest(API_ENDPOINTS.JOB_MATCH_NOTIFY, "POST", data, true),
};

// Contact Services
export const contactAPI = {
  sendMessage: (messageData) => api.post("/contact", messageData),
  getMessages: (params = {}) => api.get("/contact/messages", { params }),
  markAsRead: (messageId) => api.patch(`/contact/messages/${messageId}/read`),
  deleteMessage: (messageId) => api.delete(`/contact/messages/${messageId}`),
};

// Dashboard Services
export const dashboardAPI = {
  getModeratorDashboard: () => moderatorAPI.getDashboard(),
  getTalentSeekerDashboard: () =>
    apiRequest("/dashboard/talent-seeker", "GET", null, true),
  getEmployerDashboard: () =>
    apiRequest("/dashboard/employer", "GET", null, true),
  getJobSeekerDashboard: () =>
    apiRequest("/dashboard/job-seeker", "GET", null, true),
};

// Success Stories Services
export const successStoriesAPI = {
  getStories: (params = {}) => api.get("/success-stories", { params }),
  getStoryById: (id) => api.get(`/success-stories/${id}`),
  createStory: (storyData) => api.post("/success-stories", storyData),
  updateStory: (id, storyData) => api.put(`/success-stories/${id}`, storyData),
  deleteStory: (id) => api.delete(`/success-stories/${id}`),
};

export default api;
